import React, {useState} from "react";
import {Button, Card, CardBody, CardHeader, CardSubtitle, CardTitle, Input} from "reactstrap";
import {IExoReplacer, IExoReplacerBody, IExoReplacerForm} from "../../../../store/exo/types";
import FeatherIcon from "feather-icons-react";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {ExoService} from "../../../../services/exo.service";
import * as yup from "yup";
import {useFormik} from "formik";
import ExoReplacerList from "./ExoReplacerList";

interface FormCardExoReplacerProps {
    replacer?: IExoReplacer | null;
    isAddingReplacement?: boolean;
    onClose: () => void;
}

const FormCardExoReplacer: React.FC<FormCardExoReplacerProps> = ({
                                                                     replacer,
                                                                     isAddingReplacement = false,
                                                                     onClose,
                                                                 }) => {
    const isEditReplacer = !!replacer;
    const queryClient = useQueryClient();

    const [approvedSave, setApprovedSave] = useState<boolean>(false)

    const onSuccess = () => {
        queryClient.invalidateQueries({queryKey: ["keyExoReplacers"]})
            .then(() => onClose())
    }

    const onError = () => {
        setApprovedSave(false);
    }

    const {mutate: createReplacer, isPending: isPendingCreate} = useMutation({
        mutationFn: (data: IExoReplacerForm) => ExoService.createReplacer(data),
        onSuccess: onSuccess,
        onError: onError,
        mutationKey: ["keyCreateExoReplacer"],
    });

    const {mutate: updateReplacer, isPending: isPendingUpdate} = useMutation({
        mutationFn: (data: IExoReplacerForm) => ExoService.updateReplacer(replacer?.id!, data),
        onSuccess: onSuccess,
        onError: onError,
        mutationKey: ["keyUpdateExoReplacer"],
    });

    const handleSubmitForm = (values: IExoReplacerForm) => {
        setApprovedSave(true)

        if (!isEditReplacer) createReplacer(values)
        else updateReplacer(values)
    }

    const exoReplacementSchema: yup.ObjectSchema<IExoReplacerBody> = yup.object({
        from: yup.string()
            .trim()
            .required("'From' is required")
            .max(256, "'From' must be at most 256 characters"),
        to: yup.string()
            .trim()
            .required("'To' is required")
            .max(256, "'To' must be at most 256 characters"),
    });

    const validationSchema: yup.ObjectSchema<IExoReplacerForm> = yup.object({
        key: yup.string()
            .trim()
            .required("Key is required")
            .max(256, "Key must be at most 256 characters"),
        replacement: yup.array()
            .of(exoReplacementSchema)
            .min(1, "Replacement must contain at least 1 rule")
            .required(),
    })

    const initialValues: IExoReplacerForm = {
        key: replacer?.key || "",
        replacement: replacer?.replacement || [{from: "", to: ""}]
    };

    const formik = useFormik<IExoReplacerForm>({
        initialValues: initialValues,
        onSubmit: handleSubmitForm,
        validationSchema: validationSchema,
        validateOnBlur: true,
        validateOnChange: true,
        validateOnMount: true,
        enableReinitialize: true
    });

    const handleCancelClick = () => {
        formik.resetForm();
        onClose();
    }

    const fieldInvalid = (name: keyof IExoReplacerForm) => !!(formik.touched[name] && formik.errors[name]);

    const isPendingSave = (isPendingCreate || isPendingUpdate || !!queryClient.isFetching({queryKey: ["keyExoReplacers"]})) && approvedSave;

    return (
        <Card>
            <form onSubmit={formik.handleSubmit}>
                <CardHeader className="p-3">
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="w-75">
                            <CardSubtitle className="mb-1">
                                <small className="text-muted">
                                    Replace
                                </small>
                            </CardSubtitle>
                            <CardTitle tag="h5" className="m-0">
                                <Input
                                    id="key"
                                    name="key"
                                    placeholder={"Enter Key..."}
                                    value={formik.values.key}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    invalid={fieldInvalid("key")}
                                    disabled={isPendingSave}
                                    className="fs-5"
                                    autoFocus={!isAddingReplacement}
                                />
                            </CardTitle>
                        </div>

                        <div className="d-flex align-items-center gap-1">
                            <Button className="d-flex p-1" color="danger" onClick={handleCancelClick}
                                    disabled={isPendingSave}>
                                <FeatherIcon icon="x" size={16}/>
                            </Button>

                            <Button className="d-flex p-1" color="success" type="submit"
                                    disabled={isPendingSave}>
                                {isPendingSave ?
                                    <i className="mdi mdi-loading mdi-spin font-size-16 lh-1"></i> :
                                    <FeatherIcon icon="check" size={16}/>}
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <CardBody className="p-3">
                    <ExoReplacerList
                        value={formik.values.replacement}
                        disabled={isPendingSave}
                        isAddingReplacement={isAddingReplacement}
                        onChange={r => formik.setFieldValue("replacement", r)}/>
                </CardBody>
            </form>
        </Card>
    )
};

export default FormCardExoReplacer;